import { Button, Fieldset, Group, NumberInput, Stack, Text } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";

import {
  type NullableNumberRange,
  parseNumberInputValue,
} from "./numberInput.js";
import { useDebouncedRangeCommit } from "./useDebouncedRangeCommit.js";

export type DurationBounds = NullableNumberRange;

export interface DurationFilterProps {
  readonly maxSeconds: number;
  readonly onChange: (value: DurationBounds) => boolean | void;
  readonly unknownCount: number | null | undefined;
  readonly value: DurationBounds;
}

const MEANINGFUL_DURATION_POINTS = [
  0, 3, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 300, 450, 600, 900,
  1_200, 1_800, 2_400, 3_600, 7_200, 14_400, 28_800, 43_200, 86_400,
] as const;

function clamp(value: number, max: number): number {
  return Math.min(max, Math.max(0, value));
}

function nearestPointIndex(points: readonly number[], value: number): number {
  let best = 0;
  for (let index = 1; index < points.length; index += 1) {
    if (Math.abs(points[index]! - value) < Math.abs(points[best]! - value)) {
      best = index;
    }
  }
  return best;
}

export function DurationFilter({
  maxSeconds,
  onChange,
  unknownCount,
  value,
}: DurationFilterProps) {
  const [minimumDraft, setMinimumDraft] = useState<string | number>(value.min ?? "");
  const [maximumDraft, setMaximumDraft] = useState<string | number>(value.max ?? "");
  const resetDrafts = () => {
    setMinimumDraft(value.min ?? "");
    setMaximumDraft(value.max ?? "");
  };
  const commit = useDebouncedRangeCommit({ onChange, onRejected: resetDrafts, value });
  useEffect(() => {
    setMinimumDraft(value.min ?? "");
    setMaximumDraft(value.max ?? "");
  }, [value.max, value.min]);
  const draftBound = (
    draft: string | number,
    fallback: number | null,
  ): number | null => {
    if (draft === "") return null;
    const parsed = parseNumberInputValue(draft);
    return parsed === null ? fallback : clamp(parsed, maxSeconds);
  };
  const draftValue = {
    max: draftBound(maximumDraft, value.max),
    min: draftBound(minimumDraft, value.min),
  };
  const points = useMemo(
    () => [...new Set([
      ...MEANINGFUL_DURATION_POINTS,
      draftValue.min ?? 0,
      draftValue.max ?? maxSeconds,
      maxSeconds,
    ])]
      .filter((point) => point >= 0 && point <= maxSeconds)
      .sort((left, right) => left - right),
    [draftValue.max, draftValue.min, maxSeconds],
  );
  const minimum = clamp(draftValue.min ?? 0, maxSeconds);
  const maximum = clamp(draftValue.max ?? maxSeconds, maxSeconds);
  const minimumIndex = nearestPointIndex(points, minimum);
  const maximumIndex = nearestPointIndex(points, maximum);
  const restoreMinimumIfIncomplete = () => {
    if (minimumDraft === "" || parseNumberInputValue(minimumDraft) !== null) commit.flush();
    else {
      commit.cancel();
      setMinimumDraft(value.min ?? "");
    }
  };
  const restoreMaximumIfIncomplete = () => {
    if (maximumDraft === "" || parseNumberInputValue(maximumDraft) !== null) commit.flush();
    else {
      commit.cancel();
      setMaximumDraft(value.max ?? "");
    }
  };

  return (
    <Fieldset legend="Video duration">
      <Stack gap="sm">
        <Group align="end" grow>
          <NumberInput
            allowDecimal
            allowNegative={false}
            aria-label="Minimum duration in seconds"
            clampBehavior="strict"
            label="Minimum (seconds)"
            max={draftValue.max ?? maxSeconds}
            min={0}
            onChange={(next) => {
              const parsed = parseNumberInputValue(next);
              setMinimumDraft(next);
              if (next === "" || parsed !== null) {
                commit.schedule({
                  ...draftValue,
                  min: parsed === null ? null : clamp(parsed, maxSeconds),
                });
              } else commit.cancel();
            }}
            onBlur={restoreMinimumIfIncomplete}
            onKeyDown={(event) => {
              if (event.key === "Enter") commit.flush();
            }}
            value={minimumDraft}
          />
          <NumberInput
            allowDecimal
            allowNegative={false}
            aria-label="Maximum duration in seconds"
            clampBehavior="strict"
            label="Maximum (seconds)"
            max={maxSeconds}
            min={draftValue.min ?? 0}
            onChange={(next) => {
              const parsed = parseNumberInputValue(next);
              setMaximumDraft(next);
              if (next === "" || parsed !== null) {
                commit.schedule({
                  ...draftValue,
                  max: parsed === null ? null : clamp(parsed, maxSeconds),
                });
              } else commit.cancel();
            }}
            onBlur={restoreMaximumIfIncomplete}
            onKeyDown={(event) => {
              if (event.key === "Enter") commit.flush();
            }}
            value={maximumDraft}
          />
        </Group>

        <div className="duration-range" data-testid="duration-snap-range">
          <label className="visually-hidden" htmlFor="duration-minimum-slider">
            Minimum duration
          </label>
          <input
            aria-label="Minimum duration"
            aria-valuetext={`${minimum} seconds`}
            id="duration-minimum-slider"
            max={maximumIndex}
            min={0}
            onChange={(event) => {
              const next = points[Number(event.currentTarget.value)] ?? 0;
              setMinimumDraft(next);
              commit.schedule({ ...draftValue, min: next });
            }}
            onBlur={commit.flush}
            onKeyDown={(event) => {
              if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
              event.preventDefault();
              const delta = event.key === "ArrowRight" ? 1 : -1;
              const nextIndex = Math.min(maximumIndex, Math.max(0, minimumIndex + delta));
              if (draftValue.min === null && nextIndex === minimumIndex) return;
              const next = points[nextIndex] ?? minimum;
              setMinimumDraft(next);
              commit.schedule({ ...draftValue, min: next });
            }}
            step={1}
            type="range"
            value={Math.min(minimumIndex, maximumIndex)}
          />
          <label className="visually-hidden" htmlFor="duration-maximum-slider">
            Maximum duration
          </label>
          <input
            aria-label="Maximum duration"
            aria-valuetext={`${maximum} seconds`}
            id="duration-maximum-slider"
            max={points.length - 1}
            min={minimumIndex}
            onChange={(event) => {
              const next = points[Number(event.currentTarget.value)] ?? maxSeconds;
              setMaximumDraft(next);
              commit.schedule({ ...draftValue, max: next });
            }}
            onBlur={commit.flush}
            onKeyDown={(event) => {
              if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
              event.preventDefault();
              const delta = event.key === "ArrowRight" ? 1 : -1;
              const nextIndex = Math.min(
                points.length - 1,
                Math.max(minimumIndex, maximumIndex + delta),
              );
              if (draftValue.max === null && nextIndex === maximumIndex) return;
              const next = points[nextIndex] ?? maximum;
              setMaximumDraft(next);
              commit.schedule({ ...draftValue, max: next });
            }}
            step={1}
            type="range"
            value={Math.max(minimumIndex, maximumIndex)}
          />
        </div>

        <Button
          disabled={value.min === null && value.max === null}
          onClick={() => {
            commit.cancel();
            const accepted = onChange({ max: null, min: null });
            if (accepted !== false) {
              setMaximumDraft("");
              setMinimumDraft("");
            }
          }}
          size="compact-sm"
          variant="subtle"
        >
          Clear duration
        </Button>

        <Text c="dimmed" size="sm">
          {unknownCount === undefined || unknownCount === null
            ? "Unknown-duration counts appear with report results. Videos with unknown duration remain included and are flagged when a duration is set."
            : unknownCount === 1
              ? "1 video has unknown duration and remains included and flagged when a duration is set."
              : `${unknownCount.toLocaleString()} videos have unknown duration and remain included and flagged when a duration is set.`}
        </Text>
      </Stack>
    </Fieldset>
  );
}
